<template>
  <modal
    name="video-picker"
    height="auto"
    :scrollable="true"
    @before-open="beforeOpen"
    @before-close="beforeClose"
    :adaptive="true"
    width="95%"
    :maxWidth="1200"
  >
    <div class="modal-content py-4 text-left px-6">
      <h2 class="text-2xl font-bold mb-4 text-gray-800">Select Video</h2>

      <!-- Search Input -->
      <div class="mb-4">
        <a-input ref="quickSearch" v-model:value="quickSearch" placeholder="Search videos by name or tags..." size="large" allow-clear>
          <template #prefix>
            <search-icon size="16" class="text-gray-400" />
          </template>
        </a-input>
      </div>

      <!-- Media Grid -->
      <div v-if="Object.keys(groupedMedia).length > 0" class="max-h-[60vh] overflow-y-auto">
        <div v-for="(items, date) in groupedMedia" :key="date" class="mb-6">
          <div class="font-bold border-b text-gray-600 bg-gray-100 px-3 py-2 sticky top-0 z-10">{{ date }}</div>
          <div class="grid grid-cols-5 gap-3 mt-3">
            <thumb-card
              v-for="media in items"
              :key="media.id"
              :asset="media"
              :showLabel="true"
              class="cursor-pointer hover:ring-2 hover:ring-blue-500 rounded transition-all"
              @click="selectMedia(media)"
            />
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="flex flex-col items-center justify-center py-12 text-gray-400">
        <film-icon size="48" class="mb-4 opacity-50" />
        <p class="text-lg font-semibold">No videos found</p>
        <p class="text-sm mt-1">
          {{ quickSearch ? 'Try adjusting your search' : 'No media assets available' }}
        </p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center py-8">
        <loader-icon size="32" class="animate-spin text-blue-500" />
      </div>
    </div>
  </modal>
</template>

<script>
import { mapGetters } from 'vuex'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone.js'
import _ from 'lodash'
import ThumbCard from '@/components/mediaAssets/ThumbCard.vue'
import { FeatherIcons } from '@/scripps-components'

dayjs.extend(timezone)

const { SearchIcon, FilmIcon, LoaderIcon } = FeatherIcons

export default {
  components: {
    ThumbCard,
    SearchIcon,
    FilmIcon,
    LoaderIcon
  },
  data() {
    return {
      quickSearch: '',
      onSelect: null,
      storyId: null,
      platform: null,
      loading: false
    }
  },
  computed: {
    ...mapGetters(['currentTimezone', 'assets']),

    availableMedia() {
      // If storyId is provided, use story's media_assets
      // Otherwise, use vuex store assets (recent 500)
      if (this.storyId && this.story?.media_assets) {
        return this.story.media_assets
      }
      // Return assets from vuex store (recent 500)
      return this.assets || []
    },

    filteredMedia() {
      if (!this.quickSearch) return this.availableMedia

      const searchLower = this.quickSearch.toLowerCase()
      return this.availableMedia.filter((media) => {
        // Search by name
        if (media.name?.toString().toLowerCase().includes(searchLower)) return true
        // Search by tags
        if (media.tags?.some((tag) => tag.value?.toLowerCase().includes(searchLower))) return true
        // Search by original filename
        if (media.original?.toString().toLowerCase().includes(searchLower)) return true
        return false
      })
    },

    groupedMedia() {
      // Group media by creation date
      const grouped = _.groupBy(this.filteredMedia, (media) => {
        if (!media.created_at) return 'Unknown Date'
        return dayjs(media.created_at).tz(this.currentTimezone).format('ll')
      })

      // Sort groups by date (most recent first)
      const sorted = {}
      Object.keys(grouped)
        .sort((a, b) => {
          // Handle "Unknown Date" group - put it at the end
          if (a === 'Unknown Date') return 1
          if (b === 'Unknown Date') return -1

          const dateA = dayjs(a, 'll')
          const dateB = dayjs(b, 'll')
          return dateB - dateA
        })
        .forEach((key) => {
          sorted[key] = grouped[key]
        })

      return sorted
    },

    story() {
      // Try to find story from vuex if storyId is provided
      if (!this.storyId) return null
      return this.$store.getters.stories?.find((s) => s.id == this.storyId)
    }
  },
  methods: {
    beforeOpen(event) {
      this.onSelect = event.params?.onSelect
      this.storyId = event.params?.storyId
      this.platform = event.params?.platform
      this.quickSearch = ''

      // Focus search input after modal opens
      this.$nextTick(() => {
        this.$refs.quickSearch?.focus()
      })
    },

    beforeClose() {
      this.onSelect = null
      this.storyId = null
      this.platform = null
      this.quickSearch = ''
    },

    selectMedia(media) {
      if (this.onSelect) {
        this.onSelect(media)
      }
      this.$modal.hide('video-picker')
    }
  }
}
</script>

<style lang="postcss" scoped>
:deep(.thumb-card) {
  @apply transition-transform hover:scale-105;
}
</style>
